/**
 * Banderas de funcionalidad en el frontend (#109).
 *
 * SE PREGUNTAN AL SERVIDOR, NO SE GUARDAN EN localStorage
 * ------------------------------------------------------
 * `gpvoice_user` se escribe al entrar y vive hasta que la sesión se cierra. Si
 * las banderas viajaran ahí, encenderle una a alguien exigiría que volviera a
 * entrar — y el motivo entero de tenerlas en la base era poder moverlas en el
 * momento. Así que se piden a `/api/auth/me` al cargar la aplicación.
 *
 * LA BANDERA NO ES EL PERMISO
 * ---------------------------
 * Esto decide qué se PINTA. Cada endpoint la vuelve a verificar por su cuenta,
 * así que un usuario que edite la respuesta en su navegador verá el enlace y
 * recibirá un 403 al usarlo. Son dos capas a propósito; ver `app/banderas.py`.
 */
import { useEffect, useState } from 'react';
import api, { getBanderasDeAgente } from './api';

export const CONOCIMIENTO_EN_VIVO = 'conocimiento_en_vivo';

/**
 * `{ banderas, cargando }`. Mientras carga, TODAS se consideran apagadas.
 *
 * Eso es deliberado: pintar el enlace y quitarlo medio segundo después es peor
 * que pintarlo tarde — un enlace que aparece y desaparece parece un error de la
 * aplicación. Y si la petición falla, quedan apagadas: la consecuencia de un
 * fallo de red no puede ser ofrecer algo que el backend va a negar.
 */
export function useBanderas() {
  const [banderas, setBanderas] = useState({});
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;
    api.get('/auth/me')
      .then(r => { if (vigente) setBanderas(r.data?.banderas || {}); })
      .catch(() => { if (vigente) setBanderas({}); })
      .finally(() => { if (vigente) setCargando(false); });
    // `vigente` evita escribir estado después de desmontar, que en React 18
      // con StrictMode ocurre en el primer render doble.
    return () => { vigente = false; };
  }, []);

  return { banderas, cargando };
}

/**
 * Las banderas de OTRO usuario: el agente que un supervisor está
 * previsualizando. Admin-only del lado del servidor.
 *
 * Vive junto a `useBanderas` y no en la pantalla que lo usa para que la regla
 * siga estando en un solo lugar: las banderas se preguntan al servidor, nunca
 * se leen de `localStorage`, y mientras cargan o si la petición falla se
 * consideran TODAS apagadas. Una previsualización que ofreciera una pestaña de
 * más por un error de red es justo lo que no debe pasar.
 *
 * Devuelve además `usuarioId`, que la pantalla de Coaching necesita para acotar
 * las sesiones al agente previsualizado.
 */
export function useBanderasDeAgente(agentName) {
  const [estado, setEstado] = useState({ banderas: {}, usuarioId: null, cargando: true });

  useEffect(() => {
    if (!agentName) { setEstado({ banderas: {}, usuarioId: null, cargando: false }); return undefined; }
    let vigente = true;
    setEstado({ banderas: {}, usuarioId: null, cargando: true });
    getBanderasDeAgente(agentName)
      .then(r => {
        if (!vigente) return;
        setEstado({
          banderas: r.data?.banderas || {},
          usuarioId: r.data?.usuario_id ?? null,
          cargando: false,
        });
      })
      .catch(() => { if (vigente) setEstado({ banderas: {}, usuarioId: null, cargando: false }); });
    return () => { vigente = false; };
  }, [agentName]);

  return estado;
}

export function tieneBandera(banderas, clave) {
  return banderas?.[clave] === true;
}
